import { useEffect, useRef, useCallback, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { useUIConfig } from './ui-config/Context';
import ListFilter from './ui-config/ListFilter';
import PropEditor from './ui-config/PropEditor';
import SkeletonUI from './ui-config/skeleton-ui/SkeletonUI';
import PagesEditor from './ui-config/PagesEditor';
import { useQuery } from '../../../common/Hooks';
import BUILD_TIME_CONFIG_TYPES from '../../../config/buildTimeConfigTypes';

const SUB_TYPE_EDITORS = [BUILD_TIME_CONFIG_TYPES.APPLETS, BUILD_TIME_CONFIG_TYPES.BLOCKS, BUILD_TIME_CONFIG_TYPES.COMPONENTS, BUILD_TIME_CONFIG_TYPES.PAGES];

const MAXIMIZED_STATE = {
    Right: 'right',
    Left: 'left',
    None: 'none'
}

const UIConfigEditor = () => {
    const history = useHistory();
    const { objectSubType, objectSubTypeId } = useParams();
    const { state } = useLocation();
    const queryParams = useQuery();
    const prevObjectSubTypeRef = useRef();
    const prevObjectSubTypeIdRef = useRef();

    const prevObjectSubType = prevObjectSubTypeRef.current;
    const prevObjectSubTypeId = prevObjectSubTypeIdRef.current;

    const [maximizedState, setMaximizedState] = useState(MAXIMIZED_STATE.None);

    const { components, blocks, applets, selectedLayoutItem, selectedLayoutItemType, selectLayoutItem, selectDrillDownLayoutItem, resetSelectedLayoutItem, setBasePath, getMergedTemplate, currentOverrides, setPropEditorPath } = useUIConfig();

    useEffect(() => {
        prevObjectSubTypeRef.current = objectSubType;
        prevObjectSubTypeIdRef.current = objectSubTypeId;
    });

    useEffect(() => {
        if (prevObjectSubType === objectSubType && prevObjectSubTypeId === objectSubTypeId) {
            return;
        }

        if (!SUB_TYPE_EDITORS.includes(objectSubType) || !objectSubTypeId) {
            resetSelectedLayoutItem();
            return;
        }

        if (objectSubType === BUILD_TIME_CONFIG_TYPES.PAGES) {
            const selectedPage = queryParams.get('selectedPage');
            const sectionName = queryParams.get('sectionName');
            const configKey = queryParams.get('configKey');
            const type = queryParams.get('type');

            const initialPath = ['pages', selectedPage, 'layout', 'sections', sectionName, configKey, 'config'];
            const { path } = getMergedTemplate(initialPath, currentOverrides);
            setBasePath(path);
            setPropEditorPath(path);

            selectLayoutItem({
                type,
                name: objectSubTypeId
            });
        }
        else {
            const initialPath = [objectSubType, objectSubTypeId];
            const { path } = getMergedTemplate(initialPath, currentOverrides);
            setBasePath(path);
            setPropEditorPath(path);

            selectLayoutItem({
                type: objectSubType,
                name: objectSubTypeId
            });
        }

        if (state && state.type && state.layoutItemName && state.path) {
            selectDrillDownLayoutItem({
                type: state.type,
                name: state.layoutItemName,
                templateOverrides: state.templateOverrides
            });

            setPropEditorPath(state.path);

            return;
        }
    }, [objectSubTypeId, objectSubType, prevObjectSubType, prevObjectSubTypeId, components, blocks, applets, selectLayoutItem, state,
        resetSelectedLayoutItem, currentOverrides, history, getMergedTemplate, setBasePath, setPropEditorPath, queryParams, selectDrillDownLayoutItem]);

    const getMaximizedCols = (maximizedState, left) => {
        switch (maximizedState) {
            case MAXIMIZED_STATE.Left: return left ? 'col-12' : 'd-none';
            case MAXIMIZED_STATE.Right: return left ? 'd-none' : 'col-12';
            case MAXIMIZED_STATE.None:
            default: return left ? 'col-5' : 'col-7';
        }
    }

    const renderSkeletonEditor = useCallback((type, selectedItem) => {
        return selectedItem ?
            <>
                <div className={`${getMaximizedCols(maximizedState, true)} ps-0 h-100 border-end border-1`} style={{ overflow: 'auto' }}>
                    <SkeletonUI
                        maximizeButton={{
                            onClick: () => setMaximizedState(prev => prev !== MAXIMIZED_STATE.Left ? MAXIMIZED_STATE.Left : MAXIMIZED_STATE.None),
                            icon: maximizedState !== MAXIMIZED_STATE.Left ? "Fullscreen" : "Remove"
                        }}
                    />
                </div>
                <div className={`${getMaximizedCols(maximizedState, false)} h-100`} style={{ overflow: 'auto' }}>
                    <PropEditor
                        maximizeButton={{
                            onClick: () => setMaximizedState(prev => prev !== MAXIMIZED_STATE.Right ? MAXIMIZED_STATE.Right : MAXIMIZED_STATE.None),
                            icon: maximizedState !== MAXIMIZED_STATE.Right ? "Fullscreen" : "Remove"
                        }}
                    />
                </div>
            </>
            : <div className='col-4 ps-0 h-100' style={{ overflow: 'auto' }}>
                <ListFilter type={type} isTopLevel={true} />
            </div>
    }, [maximizedState]);

    const renderByType = useCallback((type) => {
        switch (type) {
            case BUILD_TIME_CONFIG_TYPES.PAGES: {
                if (!selectedLayoutItem) {
                    return <PagesEditor />;
                }

                if (selectedLayoutItemType === BUILD_TIME_CONFIG_TYPES.COMPONENTS) {
                    return <div>
                        <PropEditor />
                    </div>
                }

                return renderSkeletonEditor(type, true);
            }
            case BUILD_TIME_CONFIG_TYPES.APPLETS: {
                return renderSkeletonEditor(type, selectedLayoutItem);
            }
            case BUILD_TIME_CONFIG_TYPES.BLOCKS: {
                return renderSkeletonEditor(type, selectedLayoutItem);
            }
            case BUILD_TIME_CONFIG_TYPES.COMPONENTS: {
                return <>
                    <div className='col-4 ps-0 h-100' style={{ overflow: 'auto' }}>
                        <ListFilter type={type} isTopLevel={true} />
                    </div>
                    <div className='col-8 h-100' style={{ overflow: 'auto' }}>
                        {selectedLayoutItemType === BUILD_TIME_CONFIG_TYPES.COMPONENTS && <PropEditor />}
                    </div>
                </>
            }
            default: <></>
        }
    }, [selectedLayoutItem, selectedLayoutItemType, renderSkeletonEditor]);

    return (
        <div className='row m-0 h-100'>
            {renderByType(objectSubType)}
        </div>
    );
};

export default UIConfigEditor;